import { z } from 'zod';
/**
 * Reusable workflow/approval engine for LOW-RISK actions (Stage 2 scope).
 * High-risk platform commands remain out of scope by design.
 *
 * Invariants enforced by the engine, not by callers:
 *  - Separation of duties: the requester can never approve their own request.
 *  - Two-person policy requires a distinct approver before execution.
 *  - Approvals expire; an expired approval can never execute.
 *  - Execution is idempotent (idempotency key per request) and audited.
 */
export const APPROVAL_STATUSES = [
    'draft',
    'submitted',
    'approved',
    'rejected',
    'executed',
    'expired',
    'cancelled',
];
export const ApprovalStatusSchema = z.enum(APPROVAL_STATUSES);
export const APPROVAL_TRANSITIONS = {
    draft: ['submitted', 'cancelled'],
    submitted: ['approved', 'rejected', 'cancelled', 'expired'],
    approved: ['executed', 'expired', 'cancelled'],
    rejected: [],
    executed: [],
    expired: [],
    cancelled: [],
};
export const APPROVAL_ACTION_TYPES = [
    'onboarding.go_live',
    'onboarding.step.waive',
    'data.export',
    'entitlement.reduce',
    'contract.terminate',
    'billing.invoice.void',
];
export const APPROVAL_POLICIES = {
    'onboarding.go_live': {
        policy: 'two_person',
        approverRoles: ['product_owner', 'product_operations'],
        approvalTtlHours: 72,
        label: 'Take organisation live',
    },
    'onboarding.step.waive': {
        policy: 'two_person',
        approverRoles: ['product_owner', 'security_privacy_officer'],
        approvalTtlHours: 72,
        label: 'Waive onboarding step',
    },
    'data.export': {
        policy: 'single',
        approverRoles: ['product_owner', 'product_operations', 'security_privacy_officer'],
        approvalTtlHours: 24,
        label: 'Export master data',
    },
    'entitlement.reduce': {
        policy: 'two_person',
        approverRoles: ['product_owner', 'security_privacy_officer'],
        approvalTtlHours: 48,
        label: 'Reduce active entitlements',
    },
    'contract.terminate': {
        policy: 'two_person',
        approverRoles: ['product_owner', 'finance'],
        approvalTtlHours: 72,
        label: 'Terminate commercial contract',
    },
    'billing.invoice.void': {
        policy: 'two_person',
        approverRoles: ['product_owner', 'finance'],
        approvalTtlHours: 48,
        label: 'Void statutory B2B invoice',
    },
};
/** Per-action payload schemas (validated at submit AND at execute). */
export const APPROVAL_PAYLOAD_SCHEMAS = {
    'onboarding.go_live': z.object({ organisationId: z.uuid(), instanceId: z.uuid() }).strict(),
    'onboarding.step.waive': z
        .object({
        organisationId: z.uuid(),
        stepId: z.uuid(),
        waiverReason: z.string().min(10).max(1000),
        waiverExpiresAt: z.iso.datetime(),
    })
        .strict(),
    'data.export': z
        .object({ scope: z.literal('organisations'), includeArchived: z.boolean().default(false) })
        .strict(),
    'entitlement.reduce': z
        .object({
        organisationId: z.uuid(),
        contractId: z.uuid(),
        modulesToRemove: z.array(z.string()),
        capacityReductions: z.record(z.string(), z.number()),
        impactAssessmentAcknowledged: z.boolean(),
    })
        .strict(),
    'contract.terminate': z
        .object({
        organisationId: z.uuid(),
        contractId: z.uuid(),
        terminationEffectiveDate: z.iso.datetime(),
        reason: z.string().min(10).max(1000),
    })
        .strict(),
    'billing.invoice.void': z
        .object({
        organisationId: z.uuid(),
        invoiceId: z.uuid(),
        voidReason: z.string().min(10).max(1000),
    })
        .strict(),
};
export const ApprovalCreateSchema = z
    .object({
    actionType: z.enum(APPROVAL_ACTION_TYPES),
    reason: z.string().trim().min(10).max(1000),
    payload: z.record(z.string(), z.unknown()),
    /** true = submit immediately instead of leaving a draft. */
    submit: z.boolean().default(true),
})
    .strict();
export const ApprovalDecisionSchema = z
    .object({
    decision: z.enum(['approve', 'reject']),
    note: z.string().trim().min(3).max(1000),
    expectedVersion: z.number().int().min(1),
})
    .strict();
export const ApprovalRequestSchema = z.object({
    id: z.uuid(),
    actionType: z.enum(APPROVAL_ACTION_TYPES),
    actionLabel: z.string(),
    policy: z.enum(['single', 'two_person']),
    status: ApprovalStatusSchema,
    reason: z.string(),
    payload: z.record(z.string(), z.unknown()),
    organisationId: z.uuid().nullable(),
    organisationName: z.string().nullable(),
    requestedByUserId: z.uuid(),
    requestedByDisplayName: z.string().nullable(),
    submittedAt: z.iso.datetime().nullable(),
    decidedByDisplayName: z.string().nullable(),
    decidedAt: z.iso.datetime().nullable(),
    decisionNote: z.string().nullable(),
    approvalExpiresAt: z.iso.datetime().nullable(),
    executedAt: z.iso.datetime().nullable(),
    executionResult: z.string().nullable(),
    createdAt: z.iso.datetime(),
    version: z.number().int(),
});
//# sourceMappingURL=approvals.js.map